import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Modal } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import AppShell from "../layouts/AppShell";
import PdfEditor from "../components/pdf-editor/PdfEditor";
import ImageEditor from "../components/image-editor/ImageEditor";
import { GET, POST, PUT, DELETE, loading, alert, format_date, copyText, saveRefreshedToken, REFRESH_TOKEN_HEADER } from "../components/CustomComponent";

const sectionCardStyle = {
  background: "#ffffff",
  border: "1px solid rgba(18, 125, 255, 0.10)",
  borderRadius: 24,
  boxShadow: "0 14px 32px rgba(12, 118, 229, 0.06)",
};

// ไอคอน + สี ตามชนิดไฟล์ที่ backend จัดกลุ่มมาให้ (drive.service.inferFileType)
const FILE_TYPE_META = {
  image: { icon: "fa-file-image", color: "#8d5cf6" },
  pdf: { icon: "fa-file-pdf", color: "#dc2626" },
  doc: { icon: "fa-file-word", color: "#0c75e6" },
  sheet: { icon: "fa-file-excel", color: "#188251" },
  slide: { icon: "fa-file-powerpoint", color: "#e2711d" },
  archive: { icon: "fa-file-zipper", color: "#b97007" },
  video: { icon: "fa-file-video", color: "#ef5da8" },
  audio: { icon: "fa-file-audio", color: "#0ea5e9" },
  text: { icon: "fa-file-lines", color: "#566575" },
  other: { icon: "fa-file", color: "#566575" },
};

// ภาพที่ตัวแก้ภาพเปิดได้ (svg/heic/อื่นๆ ใช้ preview เดิม)
const EDITABLE_IMAGE_MIMES = ["image/jpeg", "image/png", "image/webp", "image/bmp", "image/gif"];
const EXT_TO_MIME = { jpg: "image/jpeg", jpeg: "image/jpeg", png: "image/png", webp: "image/webp", bmp: "image/bmp", gif: "image/gif" };
const isEditableImage = (item) => {
  if (item?.file_type !== "image") return false;
  const raw = String(item.mime_type || "").toLowerCase();
  const mime = raw === "image/jpg" ? "image/jpeg" : raw || EXT_TO_MIME[String(item.name || "").split(".").pop().toLowerCase()];
  return EDITABLE_IMAGE_MIMES.includes(mime);
};

const fileMeta = (type) => FILE_TYPE_META[type] || FILE_TYPE_META.other;

const formatBytes = (bytes) => {
  const size = Number(bytes) || 0;
  if (size <= 0) return "0 B";
  const units = ["B", "KB", "MB", "GB", "TB"];
  const index = Math.min(units.length - 1, Math.floor(Math.log(size) / Math.log(1024)));
  return `${(size / Math.pow(1024, index)).toFixed(index === 0 ? 0 : 1)} ${units[index]}`;
};

const readFileAsBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ""));
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

// อัปโหลดด้วย XMLHttpRequest เพราะต้องอ่าน progress ระหว่างส่ง (fetch อ่านไม่ได้)
const uploadWithProgress = (url, body, onProgress) =>
  new Promise((resolve) => {
    const request = new XMLHttpRequest();
    request.open("POST", url);
    request.setRequestHeader("Content-Type", "application/json");

    const token = localStorage.getItem("ptn-acc-tk");
    if (token) request.setRequestHeader("Authorization", atob(token));

    request.upload.onprogress = (event) => {
      if (event.lengthComputable) onProgress(Math.round((event.loaded / event.total) * 100));
    };

    request.onload = () => {
      saveRefreshedToken(request.getResponseHeader(REFRESH_TOKEN_HEADER));
      try {
        resolve(JSON.parse(request.responseText));
      } catch (error) {
        resolve({ status: false, message: "อัปโหลดไม่สำเร็จ" });
      }
    };
    request.onerror = () => resolve({ status: false, message: "เชื่อมต่อเซิร์ฟเวอร์ไม่ได้" });

    request.send(JSON.stringify(body));
  });

export default function CompanyDrive() {
  const [breadcrumb, setBreadcrumb] = useState([]);
  const [folders, setFolders] = useState([]);
  const [files, setFiles] = useState([]);
  const [usage, setUsage] = useState({ total_size: 0, total_files: 0, total_folders: 0 });

  const [currentFolder, setCurrentFolder] = useState(null); // uid ของโฟลเดอร์ที่เปิดอยู่ (null = บนสุด)
  const [viewMode, setViewMode] = useState(() => localStorage.getItem("ptn-drive-view") || "grid");
  const [searchInput, setSearchInput] = useState("");
  const [keyword, setKeyword] = useState("");

  const [selectedUid, setSelectedUid] = useState("");
  const [contextMenu, setContextMenu] = useState(null); // { x, y, item, kind }
  const [dragOverFolder, setDragOverFolder] = useState("");
  const [draggingItem, setDraggingItem] = useState(null);
  const [isDropzoneActive, setIsDropzoneActive] = useState(false);

  const [detailItem, setDetailItem] = useState(null);
  const [shareInfo, setShareInfo] = useState(null);
  const [uploads, setUploads] = useState([]); // [{ id, name, percent, status }]
  const [imageEditorFile, setImageEditorFile] = useState(null); // ไฟล์ภาพที่เปิดในหน้าแก้ภาพเต็มจอ
  const [pdfEditorFile, setPdfEditorFile] = useState(null); // ไฟล์ PDF ที่เปิดในหน้าแก้ PDF เต็มจอ

  const fileInputRef = useRef(null);
  const dragCounter = useRef(0);

  // ต้องยืนยันรหัสผ่านซ้ำทุกครั้งที่เข้าหน้านี้ ก่อนจะโหลดหรือแสดงไฟล์ใด ๆ
  const navigate = useNavigate();
  const [unlocked, setUnlocked] = useState(false);
  const [unlockPassword, setUnlockPassword] = useState("");
  const [isVerifying, setIsVerifying] = useState(false);
  const unlockInputRef = useRef(null);

  const handleUnlock = async (event) => {
    event?.preventDefault();
    const password = unlockPassword.trim();
    if (!password) {
      await alert("", "warning", "แจ้งเตือน", "กรุณากรอกรหัสผ่าน");
      unlockInputRef.current?.focus();
      return;
    }

    setIsVerifying(true);
    const result = await POST("v1/admin/users/verify-password", { password: btoa(password) });
    setIsVerifying(false);

    if (result?.status) {
      setUnlockPassword("");
      setUnlocked(true);
      return;
    }
    // POST แจ้งเตือนข้อความผิดพลาดให้แล้ว เคลียร์ช่องแล้วให้กรอกใหม่
    setUnlockPassword("");
    unlockInputRef.current?.focus();
  };

  const getData = useCallback(async () => {
    loading(true);
    const result = await GET("v1/admin/drive", {
      ...(keyword ? { data_search: keyword } : { folder_uid: currentFolder || "" }),
    });

    if (result?.status && result.data) {
      setBreadcrumb(result.data.breadcrumb || []);
      setFolders(result.data.folders || []);
      setFiles(result.data.files || []);
    }
    loading(false);
  }, [currentFolder, keyword]);

  const getUsage = useCallback(async () => {
    const result = await GET("v1/admin/drive/usage");
    if (result?.status && result.data) setUsage(result.data);
  }, []);

  useEffect(() => {
    if (unlocked) getData();
  }, [unlocked, getData]);

  useEffect(() => {
    if (unlocked) getUsage();
  }, [unlocked, getUsage]);

  // ปิดเมนูคลิกขวาเมื่อคลิกที่อื่นหรือกด Esc
  useEffect(() => {
    if (!contextMenu) return undefined;

    const close = () => setContextMenu(null);
    const onKeyDown = (event) => event.key === "Escape" && setContextMenu(null);

    window.addEventListener("click", close);
    window.addEventListener("scroll", close, true);
    window.addEventListener("keydown", onKeyDown);
    return () => {
      window.removeEventListener("click", close);
      window.removeEventListener("scroll", close, true);
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [contextMenu]);

  const switchView = (mode) => {
    setViewMode(mode);
    localStorage.setItem("ptn-drive-view", mode);
  };

  const openFolder = (uid) => {
    setKeyword("");
    setSearchInput("");
    setSelectedUid("");
    setCurrentFolder(uid || null);
  };

  /* ---------------- อัปโหลด ---------------- */

  // อัปโหลดทีละไฟล์ เพื่อให้เห็น progress แยกกันและไฟล์ใหญ่ไม่ทำให้ทั้งชุดล้ม
  const uploadFiles = async (fileList) => {
    const items = Array.from(fileList || []);
    if (!items.length) return;

    const baseId = Date.now();
    setUploads((current) => [
      ...current,
      ...items.map((file, index) => ({ id: `${baseId}-${index}`, name: file.name, percent: 0, status: "uploading" })),
    ]);

    const apiUrl = `${process.env.REACT_APP_API_URL}v1/admin/drive/files`;
    let hasSuccess = false;

    for (let index = 0; index < items.length; index += 1) {
      const file = items[index];
      const uploadId = `${baseId}-${index}`;

      try {
        const base64 = await readFileAsBase64(file);
        const result = await uploadWithProgress(
          apiUrl,
          {
            folder_uid: currentFolder || undefined,
            files: [{ file_name: file.name, mime_type: file.type || "application/octet-stream", file_base64: base64 }],
          },
          (percent) => {
            setUploads((current) => current.map((item) => (item.id === uploadId ? { ...item, percent } : item)));
          }
        );

        const ok = result?.status === true;
        if (ok) hasSuccess = true;

        setUploads((current) =>
          current.map((item) =>
            item.id === uploadId
              ? { ...item, percent: 100, status: ok ? "done" : "error", message: ok ? "" : result?.message || "อัปโหลดไม่สำเร็จ" }
              : item
          )
        );
      } catch (error) {
        setUploads((current) =>
          current.map((item) => (item.id === uploadId ? { ...item, status: "error", message: "อ่านไฟล์ไม่สำเร็จ" } : item))
        );
      }
    }

    if (hasSuccess) {
      getData();
      getUsage();
    }
  };

  const handleFilePicked = (event) => {
    uploadFiles(event.target.files);
    event.target.value = "";
  };

  /* ---------------- ลากไฟล์จากเครื่องมาวาง ---------------- */

  const handleDragEnter = (event) => {
    // สนใจเฉพาะการลากไฟล์จากนอกหน้าเว็บ ไม่ใช่การลากไอเทมในไดรฟ์
    if (!event.dataTransfer?.types?.includes("Files")) return;
    dragCounter.current += 1;
    setIsDropzoneActive(true);
  };

  const handleDragLeave = (event) => {
    if (!event.dataTransfer?.types?.includes("Files")) return;
    dragCounter.current -= 1;
    if (dragCounter.current <= 0) {
      dragCounter.current = 0;
      setIsDropzoneActive(false);
    }
  };

  const handleDrop = (event) => {
    if (!event.dataTransfer?.types?.includes("Files")) return;
    event.preventDefault();
    dragCounter.current = 0;
    setIsDropzoneActive(false);
    uploadFiles(event.dataTransfer.files);
  };

  /* ---------------- ลากย้ายไอเทมภายในไดรฟ์ ---------------- */

  const handleItemDragStart = (item, kind) => {
    setDraggingItem({ ...item, kind });
  };

  const handleFolderDrop = async (event, targetFolderUid) => {
    event.preventDefault();
    event.stopPropagation();
    setDragOverFolder("");

    const item = draggingItem;
    setDraggingItem(null);
    if (!item || item.uid === targetFolderUid) return;

    await moveItem(item, targetFolderUid);
  };

  const moveItem = async (item, targetUid) => {
    loading(true);
    const path = item.kind === "folder" ? "folders" : "files";
    const result = await PUT(`v1/admin/drive/${path}/${item.uid}/move`, { target_uid: targetUid || "" });
    loading(false);

    if (result?.status) {
      getData();
    } else {
      await alert("", "warning", "แจ้งเตือน", result?.message || "ย้ายไม่สำเร็จ");
    }
  };

  /* ---------------- คำสั่งบนเมนูคลิกขวา ---------------- */

  const openContextMenu = (event, item, kind) => {
    event.preventDefault();
    event.stopPropagation();
    setSelectedUid(item.uid);
    // กันเมนูล้นขอบจอ
    const menuWidth = 220;
    const menuHeight = kind === "folder" ? 220 : item.file_type === "pdf" || isEditableImage(item) ? 340 : 300;
    setContextMenu({
      x: Math.min(event.clientX, window.innerWidth - menuWidth),
      y: Math.min(event.clientY, window.innerHeight - menuHeight),
      item,
      kind,
    });
  };

  const handleDownload = async (item) => {
    const result = await GET(`v1/admin/drive/files/${item.uid}/url`, { disposition: "attachment" });
    if (result?.status && result.data?.url) {
      window.open(result.data.url, "_blank", "noopener");
    } else {
      await alert("", "warning", "แจ้งเตือน", result?.message || "ดาวน์โหลดไม่สำเร็จ");
    }
  };

  // ไฟล์ PDF เปิดในหน้าแก้ PDF เต็มจอ (overlay ในหน้านี้ ไม่เปลี่ยน route เพราะต้องปลดล็อกรหัสผ่านทุกครั้ง)
  const handleEditPdf = (item) => {
    setContextMenu(null);
    setPdfEditorFile({ uid: item.uid, name: item.name, folder_uid: item.folder_uid || null });
  };

  const handleEditImage = (item) => {
    setContextMenu(null);
    setImageEditorFile({ uid: item.uid, name: item.name, folder_uid: item.folder_uid || null, mime_type: item.mime_type || null });
  };

  const handleOpenFile = (item) => {
    if (item.file_type === "pdf") handleEditPdf(item);
    else if (isEditableImage(item)) handleEditImage(item);
    else handlePreview(item);
  };

  const handlePreview = async (item) => {
    const result = await GET(`v1/admin/drive/files/${item.uid}/url`);
    if (result?.status && result.data?.url) {
      window.open(result.data.url, "_blank", "noopener");
    }
  };

  const handleRename = async (item, kind) => {
    const nextName = window.prompt(kind === "folder" ? "ชื่อโฟลเดอร์ใหม่" : "ชื่อไฟล์ใหม่", item.name);
    if (nextName === null || !nextName.trim() || nextName.trim() === item.name) return;

    loading(true);
    const path = kind === "folder" ? "folders" : "files";
    const result = await PUT(`v1/admin/drive/${path}/${item.uid}`, { name: nextName.trim() });
    loading(false);

    if (result?.status) {
      getData();
    } else {
      await alert("", "warning", "แจ้งเตือน", result?.message || "เปลี่ยนชื่อไม่สำเร็จ");
    }
  };

  const handleCopy = async (item) => {
    loading(true);
    const result = await POST(`v1/admin/drive/files/${item.uid}/copy`, {});
    loading(false);

    if (result?.status) {
      getData();
      getUsage();
    } else {
      await alert("", "warning", "แจ้งเตือน", result?.message || "ทำสำเนาไม่สำเร็จ");
    }
  };

  const handleShare = async (item) => {
    loading(true);
    const result = await POST(`v1/admin/drive/files/${item.uid}/share`, { expire_days: 7 });
    loading(false);

    if (result?.status && result.data) {
      setShareInfo({ ...result.data, name: item.name, uid: item.uid });
      getData();
    } else {
      await alert("", "warning", "แจ้งเตือน", result?.message || "สร้างลิงก์แชร์ไม่สำเร็จ");
    }
  };

  const handleRevokeShare = async (uid) => {
    loading(true);
    const result = await DELETE(`v1/admin/drive/files/${uid}/share`);
    loading(false);

    if (result?.status) {
      setShareInfo(null);
      getData();
      await alert("", "success", "สำเร็จ", "ยกเลิกลิงก์แชร์แล้ว");
    }
  };

  const handleDelete = async (item, kind) => {
    const message =
      kind === "folder"
        ? `ลบโฟลเดอร์ "${item.name}" พร้อมไฟล์และโฟลเดอร์ย่อยทั้งหมดข้างใน?`
        : `ลบไฟล์ "${item.name}" ?`;

    if (!(await alert("", "warning", "ยืนยันการลบ", message, "ลบ", "ยกเลิก"))) return;

    loading(true);
    const path = kind === "folder" ? "folders" : "files";
    const result = await DELETE(`v1/admin/drive/${path}/${item.uid}`);
    loading(false);

    if (result?.status) {
      getData();
      getUsage();
    } else {
      await alert("", "warning", "แจ้งเตือน", result?.message || "ลบไม่สำเร็จ");
    }
  };

  const handleCreateFolder = async () => {
    const name = window.prompt("ชื่อโฟลเดอร์ใหม่", "โฟลเดอร์ใหม่");
    if (name === null || !name.trim()) return;

    loading(true);
    const result = await POST("v1/admin/drive/folders", {
      name: name.trim(),
      parent_uid: currentFolder || undefined,
    });
    loading(false);

    if (result?.status) {
      getData();
      getUsage();
    } else {
      await alert("", "warning", "แจ้งเตือน", result?.message || "สร้างโฟลเดอร์ไม่สำเร็จ");
    }
  };

  const activeUploads = uploads.filter((item) => item.status === "uploading").length;
  const isEmpty = !folders.length && !files.length;

  const contextActions = useMemo(() => {
    if (!contextMenu) return [];
    const { item, kind } = contextMenu;

    const common = [
      { label: "ดูรายละเอียด", icon: "fa-circle-info", onClick: () => setDetailItem({ ...item, kind }) },
    ];

    if (kind === "folder") {
      return [
        { label: "เปิดโฟลเดอร์", icon: "fa-folder-open", onClick: () => openFolder(item.uid) },
        ...common,
        { label: "เปลี่ยนชื่อ", icon: "fa-pen", onClick: () => handleRename(item, "folder") },
        { divider: true },
        { label: "ลบ", icon: "fa-trash", danger: true, onClick: () => handleDelete(item, "folder") },
      ];
    }

    return [
      ...common,
      ...(item.file_type === "pdf" ? [{ label: "แก้ไข PDF", icon: "fa-file-pen", onClick: () => handleEditPdf(item) }] : []),
      ...(isEditableImage(item) ? [{ label: "แก้ไขภาพ", icon: "fa-wand-magic-sparkles", onClick: () => handleEditImage(item) }] : []),
      { label: "ดาวน์โหลด", icon: "fa-download", onClick: () => handleDownload(item) },
      { label: "เปลี่ยนชื่อ", icon: "fa-pen", onClick: () => handleRename(item, "file") },
      { label: "ทำสำเนา", icon: "fa-copy", onClick: () => handleCopy(item) },
      { label: "แชร์", icon: "fa-share-nodes", onClick: () => handleShare(item) },
      { divider: true },
      { label: "ลบ", icon: "fa-trash", danger: true, onClick: () => handleDelete(item, "file") },
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contextMenu]);

  const renderFolderCell = (folder) => (
    <>
      <div
        className="d-flex align-items-center justify-content-center flex-shrink-0"
        style={{ width: 40, height: 40, borderRadius: 10, background: "rgba(245, 166, 35, 0.16)", color: "#e2a03f", fontSize: 18 }}
      >
        <i className="fa-solid fa-folder" />
      </div>
      <div className="text-truncate">
        <div className="fw-semibold text-truncate" style={{ color: "#22384a" }}>{folder.name}</div>
        <div className="small text-secondary">
          {folder.folder_count || 0} โฟลเดอร์ · {folder.file_count || 0} ไฟล์
        </div>
      </div>
    </>
  );

  const renderFileCell = (file) => {
    const meta = fileMeta(file.file_type);
    return (
      <>
        <div
          className="d-flex align-items-center justify-content-center flex-shrink-0"
          style={{ width: 40, height: 40, borderRadius: 10, background: `${meta.color}1f`, color: meta.color, fontSize: 18 }}
        >
          <i className={`fa-solid ${meta.icon}`} />
        </div>
        <div className="text-truncate">
          <div className="fw-semibold text-truncate" style={{ color: "#22384a" }}>{file.name}</div>
          <div className="small text-secondary">
            {formatBytes(file.file_size)}
            {file.is_shared ? <span className="ms-2" style={{ color: "#188251" }}><i className="fa-solid fa-link me-1" />แชร์อยู่</span> : null}
          </div>
        </div>
      </>
    );
  };

  if (!unlocked) {
    return (
      <AppShell title="ไฟล์ข้อมูลบริษัท" description="พื้นที่เก็บไฟล์ส่วนกลางของบริษัท สร้างโฟลเดอร์ ลากไฟล์มาวางเพื่ออัปโหลด และแชร์ลิงก์ให้คนนอกได้">
        <div className="d-flex justify-content-center py-5">
          <form className="p-4 p-md-5 w-100" style={{ ...sectionCardStyle, maxWidth: 440 }} onSubmit={handleUnlock}>
            <div className="text-center mb-4">
              <div
                className="d-inline-flex align-items-center justify-content-center mb-3"
                style={{ width: 64, height: 64, borderRadius: 20, background: "rgba(12, 118, 229, 0.10)", color: "#0c76e5", fontSize: 26 }}
              >
                <i className="fa-solid fa-lock" />
              </div>
              <h5 className="fw-semibold mb-1" style={{ color: "#22384a" }}>ยืนยันตัวตนก่อนเข้าใช้งาน</h5>
              <div className="small text-secondary">ไฟล์ข้อมูลบริษัทเป็นข้อมูลสำคัญ กรุณากรอกรหัสผ่านอีกครั้ง</div>
            </div>

            <label htmlFor="drive-unlock-password" className="form-label fw-semibold small" style={{ color: "#22384a" }}>รหัสผ่าน</label>
            <input
              id="drive-unlock-password"
              ref={unlockInputRef}
              type="password"
              className="form-control mb-4"
              value={unlockPassword}
              onChange={(event) => setUnlockPassword(event.target.value)}
              placeholder="กรอกรหัสผ่านที่ใช้เข้าสู่ระบบ"
              autoComplete="current-password"
              autoFocus
              disabled={isVerifying}
            />

            <div className="d-flex gap-2">
              <button type="button" className="btn btn-outline-secondary w-50" onClick={() => navigate(-1)} disabled={isVerifying}>
                ยกเลิก
              </button>
              <button type="submit" className="btn btn-primary w-50" disabled={isVerifying}>
                {isVerifying ? <span className="spinner-border spinner-border-sm me-2" /> : <i className="fa-solid fa-unlock me-2" />}
                ยืนยัน
              </button>
            </div>
          </form>
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell title="ไฟล์ข้อมูลบริษัท" description="พื้นที่เก็บไฟล์ส่วนกลางของบริษัท สร้างโฟลเดอร์ ลากไฟล์มาวางเพื่ออัปโหลด และแชร์ลิงก์ให้คนนอกได้">
      {/* แถบเครื่องมือ */}
      <div className="d-flex flex-column flex-lg-row justify-content-between align-items-lg-center gap-3 mb-3">
        <div className="d-flex gap-2 flex-wrap align-items-center filters-mobile-stack">
          <input
            type="text"
            className="form-control"
            value={searchInput}
            onChange={(event) => setSearchInput(event.target.value)}
            onKeyDown={(event) => event.key === "Enter" && setKeyword(searchInput.trim())}
            placeholder="ค้นหาไฟล์และโฟลเดอร์ทั้งไดรฟ์..."
            style={{ width: "280px", borderRadius: "12px" }}
          />
          <button type="button" className="btn btn-primary px-4" style={{ borderRadius: 12 }} onClick={() => setKeyword(searchInput.trim())}>
            ค้นหา
          </button>
          {keyword && (
            <button
              type="button"
              className="btn btn-light px-4"
              style={{ borderRadius: 12, border: "1px solid rgba(18, 125, 255, 0.14)", color: "#4f6d83" }}
              onClick={() => {
                setKeyword("");
                setSearchInput("");
              }}
            >
              ล้างการค้นหา
            </button>
          )}
        </div>

        <div className="d-flex gap-2 flex-wrap align-items-center">
          <div className="btn-group" role="group">
            <button
              type="button"
              className={`btn btn-sm ${viewMode === "grid" ? "btn-primary" : "btn-light"}`}
              style={viewMode === "grid" ? undefined : { border: "1px solid rgba(18, 125, 255, 0.14)", color: "#4f6d83" }}
              onClick={() => switchView("grid")}
              title="มุมมองการ์ด"
            >
              <i className="fa-solid fa-table-cells-large" />
            </button>
            <button
              type="button"
              className={`btn btn-sm ${viewMode === "list" ? "btn-primary" : "btn-light"}`}
              style={viewMode === "list" ? undefined : { border: "1px solid rgba(18, 125, 255, 0.14)", color: "#4f6d83" }}
              onClick={() => switchView("list")}
              title="มุมมองรายการ"
            >
              <i className="fa-solid fa-list" />
            </button>
          </div>

          <button type="button" className="btn btn-light px-3" style={{ borderRadius: 12, border: "1px solid rgba(18, 125, 255, 0.14)", color: "#0b4f84" }} onClick={handleCreateFolder}>
            <i className="fa-solid fa-folder-plus me-2" />สร้างโฟลเดอร์
          </button>
          <button type="button" className="btn btn-primary px-4" style={{ borderRadius: 12 }} onClick={() => fileInputRef.current?.click()}>
            <i className="fa-solid fa-cloud-arrow-up me-2" />อัปโหลดไฟล์
          </button>
          <input ref={fileInputRef} type="file" multiple className="d-none" onChange={handleFilePicked} />
        </div>
      </div>

      {/* เส้นทางโฟลเดอร์ */}
      <div className="d-flex align-items-center gap-2 flex-wrap mb-3 small">
        {keyword ? (
          <span className="text-secondary">
            <i className="fa-solid fa-magnifying-glass me-2" />
            ผลการค้นหา "{keyword}" — {folders.length} โฟลเดอร์ · {files.length} ไฟล์
          </span>
        ) : (
          <>
            <button
              type="button"
              className="btn btn-sm btn-link p-0 text-decoration-none fw-semibold"
              style={{ color: currentFolder ? "#0c75e6" : "#22384a" }}
              onClick={() => openFolder(null)}
              onDragOver={(event) => {
                if (draggingItem) event.preventDefault();
              }}
              onDrop={(event) => handleFolderDrop(event, "")}
            >
              <i className="fa-solid fa-hard-drive me-2" />ไดรฟ์บริษัท
            </button>
            {breadcrumb.map((crumb, index) => (
              <React.Fragment key={crumb.uid}>
                <i className="fa-solid fa-chevron-right text-secondary" style={{ fontSize: 10 }} />
                <button
                  type="button"
                  className="btn btn-sm btn-link p-0 text-decoration-none fw-semibold"
                  style={{ color: index === breadcrumb.length - 1 ? "#22384a" : "#0c75e6" }}
                  onClick={() => openFolder(crumb.uid)}
                  onDragOver={(event) => {
                    if (draggingItem) event.preventDefault();
                  }}
                  onDrop={(event) => handleFolderDrop(event, crumb.uid)}
                >
                  {crumb.name}
                </button>
              </React.Fragment>
            ))}
          </>
        )}

        <span className="ms-auto text-secondary">
          ใช้พื้นที่ {formatBytes(usage.total_size)} · {usage.total_files} ไฟล์ · {usage.total_folders} โฟลเดอร์
        </span>
      </div>

      {/* กล่องรายการ + พื้นที่ลากไฟล์มาวาง */}
      <div
        className="p-3 p-md-4 drive-dropzone"
        style={sectionCardStyle}
        onDragEnter={handleDragEnter}
        onDragOver={(event) => event.dataTransfer?.types?.includes("Files") && event.preventDefault()}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={() => setSelectedUid("")}
      >
        {isDropzoneActive && (
          <div className="drive-dropzone-overlay">
            <i className="fa-solid fa-cloud-arrow-up" style={{ fontSize: 34 }} />
            <div>วางไฟล์ที่นี่เพื่ออัปโหลด{breadcrumb.length ? ` ลงโฟลเดอร์ "${breadcrumb[breadcrumb.length - 1].name}"` : ""}</div>
          </div>
        )}

        {isEmpty ? (
          <div className="text-center text-secondary py-5">
            <i className="fa-solid fa-folder-open mb-3" style={{ fontSize: 40, opacity: 0.4 }} />
            <div>{keyword ? "ไม่พบไฟล์หรือโฟลเดอร์ที่ค้นหา" : "โฟลเดอร์นี้ยังว่าง — ลากไฟล์มาวางที่นี่ หรือกดปุ่มอัปโหลดด้านบน"}</div>
          </div>
        ) : viewMode === "grid" ? (
          <>
            {folders.length > 0 && (
              <>
                <div className="small text-uppercase text-secondary fw-semibold mb-2">โฟลเดอร์</div>
                <div className="row g-3 mb-4">
                  {folders.map((folder) => (
                    <div className="col-6 col-md-4 col-xl-3" key={folder.uid}>
                      <div
                        className={`drive-card drive-item d-flex align-items-center gap-3 ${selectedUid === folder.uid ? "is-selected" : ""} ${dragOverFolder === folder.uid ? "is-drop-target" : ""} ${draggingItem?.uid === folder.uid ? "is-dragging" : ""}`}
                        draggable
                        onDragStart={() => handleItemDragStart(folder, "folder")}
                        onDragEnd={() => setDraggingItem(null)}
                        onDragOver={(event) => {
                          if (!draggingItem || draggingItem.uid === folder.uid) return;
                          event.preventDefault();
                          event.stopPropagation();
                          setDragOverFolder(folder.uid);
                        }}
                        onDragLeave={() => setDragOverFolder("")}
                        onDrop={(event) => handleFolderDrop(event, folder.uid)}
                        onClick={(event) => {
                          event.stopPropagation();
                          setSelectedUid(folder.uid);
                        }}
                        onDoubleClick={() => openFolder(folder.uid)}
                        onContextMenu={(event) => openContextMenu(event, folder, "folder")}
                      >
                        {renderFolderCell(folder)}
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}

            {files.length > 0 && (
              <>
                <div className="small text-uppercase text-secondary fw-semibold mb-2">ไฟล์</div>
                <div className="row g-3">
                  {files.map((file) => (
                    <div className="col-6 col-md-4 col-xl-3" key={file.uid}>
                      <div
                        className={`drive-card drive-item d-flex align-items-center gap-3 ${selectedUid === file.uid ? "is-selected" : ""} ${draggingItem?.uid === file.uid ? "is-dragging" : ""}`}
                        draggable
                        onDragStart={() => handleItemDragStart(file, "file")}
                        onDragEnd={() => setDraggingItem(null)}
                        onClick={(event) => {
                          event.stopPropagation();
                          setSelectedUid(file.uid);
                        }}
                        onDoubleClick={() => handleOpenFile(file)}
                        onContextMenu={(event) => openContextMenu(event, file, "file")}
                      >
                        {renderFileCell(file)}
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </>
        ) : (
          <div className="table-responsive">
            <table className="table table-hover align-middle mb-0">
              <thead className="table-light">
                <tr>
                  <th className="text-secondary fw-medium">ชื่อ</th>
                  <th className="text-secondary fw-medium text-nowrap">ประเภท</th>
                  <th className="text-secondary fw-medium text-end text-nowrap">ขนาด</th>
                  <th className="text-secondary fw-medium text-nowrap">แก้ไขล่าสุด</th>
                  <th className="text-secondary fw-medium text-center">จัดการ</th>
                </tr>
              </thead>
              <tbody>
                {folders.map((folder) => (
                  <tr
                    key={folder.uid}
                    className={`drive-item ${selectedUid === folder.uid ? "is-selected" : ""} ${dragOverFolder === folder.uid ? "is-drop-target" : ""}`}
                    draggable
                    onDragStart={() => handleItemDragStart(folder, "folder")}
                    onDragEnd={() => setDraggingItem(null)}
                    onDragOver={(event) => {
                      if (!draggingItem || draggingItem.uid === folder.uid) return;
                      event.preventDefault();
                      event.stopPropagation();
                      setDragOverFolder(folder.uid);
                    }}
                    onDragLeave={() => setDragOverFolder("")}
                    onDrop={(event) => handleFolderDrop(event, folder.uid)}
                    onClick={(event) => {
                      event.stopPropagation();
                      setSelectedUid(folder.uid);
                    }}
                    onDoubleClick={() => openFolder(folder.uid)}
                    onContextMenu={(event) => openContextMenu(event, folder, "folder")}
                  >
                    <td>
                      <div className="d-flex align-items-center gap-3">{renderFolderCell(folder)}</div>
                    </td>
                    <td className="text-nowrap">โฟลเดอร์</td>
                    <td className="text-end">-</td>
                    <td className="text-nowrap">{format_date(folder.updated_at || folder.created_at, "dd/mm/yyyy hh:mm")}</td>
                    <td className="text-center">
                      <button className="btn btn-sm btn-outline-secondary rounded-3" title="เมนู" onClick={(event) => openContextMenu(event, folder, "folder")}>
                        <i className="fa-solid fa-ellipsis-vertical" />
                      </button>
                    </td>
                  </tr>
                ))}

                {files.map((file) => (
                  <tr
                    key={file.uid}
                    className={`drive-item ${selectedUid === file.uid ? "is-selected" : ""}`}
                    draggable
                    onDragStart={() => handleItemDragStart(file, "file")}
                    onDragEnd={() => setDraggingItem(null)}
                    onClick={(event) => {
                      event.stopPropagation();
                      setSelectedUid(file.uid);
                    }}
                    onDoubleClick={() => handleOpenFile(file)}
                    onContextMenu={(event) => openContextMenu(event, file, "file")}
                  >
                    <td>
                      <div className="d-flex align-items-center gap-3">{renderFileCell(file)}</div>
                    </td>
                    <td className="text-nowrap">{file.file_type || "-"}</td>
                    <td className="text-end text-nowrap">{formatBytes(file.file_size)}</td>
                    <td className="text-nowrap">{format_date(file.updated_at || file.created_at, "dd/mm/yyyy hh:mm")}</td>
                    <td className="text-center">
                      <button className="btn btn-sm btn-outline-secondary rounded-3" title="เมนู" onClick={(event) => openContextMenu(event, file, "file")}>
                        <i className="fa-solid fa-ellipsis-vertical" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* เมนูคลิกขวา */}
      {contextMenu && (
        <div className="drive-context-menu" style={{ top: contextMenu.y, left: contextMenu.x }} onClick={(event) => event.stopPropagation()}>
          {contextActions.map((action, index) =>
            action.divider ? (
              <div className="drive-context-divider" key={`divider-${index}`} />
            ) : (
              <button
                type="button"
                key={action.label}
                className={action.danger ? "is-danger" : ""}
                onClick={() => {
                  setContextMenu(null);
                  action.onClick();
                }}
              >
                <i className={`fa-solid ${action.icon}`} style={{ width: 16 }} />
                {action.label}
              </button>
            )
          )}
        </div>
      )}

      {/* กล่องสถานะอัปโหลดมุมขวาล่าง */}
      {uploads.length > 0 && (
        <div className="drive-upload-toast">
          <div className="drive-upload-toast-head d-flex align-items-center justify-content-between">
            <span className="fw-semibold">
              {activeUploads > 0 ? `กำลังอัปโหลด ${activeUploads} ไฟล์` : `อัปโหลดเสร็จแล้ว ${uploads.length} ไฟล์`}
            </span>
            <button
              type="button"
              className="btn btn-sm p-0 text-white"
              style={{ background: "transparent", border: "none" }}
              onClick={() => setUploads(activeUploads > 0 ? uploads.filter((item) => item.status === "uploading") : [])}
              title="ปิด"
            >
              <i className="fa-solid fa-xmark" />
            </button>
          </div>
          <div className="drive-upload-toast-body">
            {uploads.map((item) => (
              <div className="px-3 py-2" key={item.id}>
                <div className="d-flex align-items-center justify-content-between gap-2">
                  <span className="text-truncate small" style={{ color: "#22384a" }}>{item.name}</span>
                  {item.status === "done" && <i className="fa-solid fa-circle-check" style={{ color: "#188251" }} />}
                  {item.status === "error" && <i className="fa-solid fa-circle-exclamation" style={{ color: "#b63636" }} />}
                  {item.status === "uploading" && <span className="small text-secondary">{item.percent}%</span>}
                </div>
                {item.status === "uploading" && (
                  <div className="progress mt-1" style={{ height: 4, background: "rgba(15,102,182,0.08)" }}>
                    <div className="progress-bar" style={{ width: `${item.percent}%`, background: "#0c75e6" }} />
                  </div>
                )}
                {item.status === "error" && <div className="small" style={{ color: "#b63636" }}>{item.message}</div>}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* รายละเอียดไฟล์/โฟลเดอร์ */}
      <Modal show={!!detailItem} onHide={() => setDetailItem(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title as="h5" className="fw-semibold">รายละเอียด</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {detailItem && (
            <>
              <div className="d-flex align-items-center gap-3 mb-4">
                <div
                  className="d-flex align-items-center justify-content-center flex-shrink-0"
                  style={{
                    width: 56,
                    height: 56,
                    borderRadius: 14,
                    background: detailItem.kind === "folder" ? "rgba(245, 166, 35, 0.16)" : `${fileMeta(detailItem.file_type).color}1f`,
                    color: detailItem.kind === "folder" ? "#e2a03f" : fileMeta(detailItem.file_type).color,
                    fontSize: 24,
                  }}
                >
                  <i className={`fa-solid ${detailItem.kind === "folder" ? "fa-folder" : fileMeta(detailItem.file_type).icon}`} />
                </div>
                <div className="text-truncate">
                  <div className="fw-bold" style={{ color: "#22384a", wordBreak: "break-word" }}>{detailItem.name}</div>
                  <div className="small text-secondary">{detailItem.kind === "folder" ? "โฟลเดอร์" : detailItem.mime_type || detailItem.file_type}</div>
                </div>
              </div>

              {[
                { label: "ประเภท", value: detailItem.kind === "folder" ? "โฟลเดอร์" : detailItem.file_type },
                { label: "ขนาด", value: detailItem.kind === "folder" ? "-" : formatBytes(detailItem.file_size) },
                { label: "อยู่ในโฟลเดอร์", value: detailItem.folder_uid ? breadcrumb[breadcrumb.length - 1]?.name || "-" : "ไดรฟ์บริษัท" },
                { label: "สร้างเมื่อ", value: format_date(detailItem.created_at, "dd/mm/yyyy hh:mm") },
                { label: "แก้ไขล่าสุด", value: format_date(detailItem.updated_at || detailItem.created_at, "dd/mm/yyyy hh:mm") },
                ...(detailItem.kind === "folder"
                  ? [{ label: "ข้างใน", value: `${detailItem.folder_count || 0} โฟลเดอร์ · ${detailItem.file_count || 0} ไฟล์` }]
                  : [{ label: "สถานะแชร์", value: detailItem.is_shared ? `แชร์อยู่${detailItem.share_expire ? ` (ถึง ${format_date(detailItem.share_expire, "dd/mm/yyyy")})` : ""}` : "ไม่ได้แชร์" }]),
              ].map((row) => (
                <div className="d-flex justify-content-between border-bottom py-2" key={row.label}>
                  <span className="text-secondary small">{row.label}</span>
                  <span className="fw-semibold text-end" style={{ wordBreak: "break-word" }}>{row.value}</span>
                </div>
              ))}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          {detailItem?.kind === "file" && (
            <button className="btn btn-outline-primary" onClick={() => handleDownload(detailItem)}>
              <i className="fa-solid fa-download me-2" />ดาวน์โหลด
            </button>
          )}
          <button className="btn btn-secondary" onClick={() => setDetailItem(null)}>ปิด</button>
        </Modal.Footer>
      </Modal>

      {/* ลิงก์แชร์ */}
      <Modal show={!!shareInfo} onHide={() => setShareInfo(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title as="h5" className="fw-semibold">แชร์ไฟล์</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {shareInfo && (
            <>
              <div className="fw-semibold mb-1" style={{ color: "#22384a", wordBreak: "break-word" }}>{shareInfo.name}</div>
              <div className="small text-secondary mb-3">
                ใครมีลิงก์นี้เปิดไฟล์ได้โดยไม่ต้องเข้าสู่ระบบ
                {shareInfo.share_expire ? ` ลิงก์หมดอายุ ${format_date(shareInfo.share_expire, "dd/mm/yyyy")}` : " ลิงก์ไม่มีวันหมดอายุ"}
              </div>
              <div className="input-group">
                <input type="text" className="form-control" readOnly value={shareInfo.share_url} onFocus={(event) => event.target.select()} />
                <button className="btn btn-primary" onClick={() => copyText(shareInfo.share_url)}>
                  <i className="fa-solid fa-copy me-2" />คัดลอก
                </button>
              </div>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <button className="btn btn-outline-danger" onClick={() => handleRevokeShare(shareInfo.uid)}>ยกเลิกลิงก์</button>
          <button className="btn btn-secondary" onClick={() => setShareInfo(null)}>ปิด</button>
        </Modal.Footer>
      </Modal>

      {imageEditorFile ? (
        <ImageEditor
          key={imageEditorFile.uid}
          file={imageEditorFile}
          onClose={() => setImageEditorFile(null)}
          onSaved={() => {
            getData();
            getUsage();
          }}
        />
      ) : null}

      {pdfEditorFile ? (
        <PdfEditor
          key={pdfEditorFile.uid}
          file={pdfEditorFile}
          onClose={() => setPdfEditorFile(null)}
          onSaved={() => {
            getData();
            getUsage();
          }}
        />
      ) : null}
    </AppShell>
  );
}
